import React, { ReactNode } from 'react';
import {
  ContactOption,
  CustomerContactDetails,
  Dealer,
  ICarSearchType,
  MatchMakerAnswers
} from '../shared/types';
import { MIN_MONTHLY_BUDGET } from './lib/helpers/constants';
import { TransactionStatusType } from '@bus/models';

export enum JOURNEY_STATE {
  STARTED,
  MM_FINISHED,
  FINANCING,
  DEALER_SELECTION,
  CONTACT_DETAILS,
  SUMMARY,
  CONFIRMATION
}

export enum FlowType {
  SHORT = 'short',
  LONG = 'long',
  HYBRID = 'hybrid', // User previously selected long journey, but changed mind and selected short - it affects field visibility on summary,
  UNSELECTED = 'unselected'
}

export type EditableSummarySection =
  | 'characteristics'
  | 'finance'
  | 'features'
  | 'additional-info'
  | 'dealer'
  | 'personal-details';

export interface RenewalApplicationState {
  matchMakerAnswers: MatchMakerAnswers;
  customerInformation: CustomerContactDetails;
  dealer?: Dealer;
  transactionId: string;
  transactionStatus: TransactionStatusType;
  sessionToken: string;
  preSelectedDealerId: string;
  currentJourneyStatus: JOURNEY_STATE;
  isEditMode: boolean;
  legalEntity: string;
  flowType: FlowType;
  editedSections: Array<EditableSummarySection>;
  skip: boolean;
  hasSubmittedMM1: boolean;
}

function getMergedSearchParams() {
  const beforeHash = new URLSearchParams(window.location.search);
  const afterHash = new URLSearchParams(window.location.hash.split('?')[1] || '');

  const merged = new URLSearchParams(beforeHash);
  Array.from(afterHash.entries()).forEach(([key, value]) => {
    merged.set(key, value);
  });

  return merged;
}

const TRANSACTION_ID_PARAM_NAME = 'transactionId';
const SESSION_TOKEN_PARAM_NAME = 'token';
const DEALER_ID_PARAM_NAME = 'DealerCompanyNumber';
const LEGAL_ENTITY_PARAM_NAME = 'LegalEntity';
const searchParams = getMergedSearchParams();

let transactionIdParameter = '';
if (searchParams.has(TRANSACTION_ID_PARAM_NAME)) {
  transactionIdParameter = searchParams.get(TRANSACTION_ID_PARAM_NAME) ?? '';
}

let sessionTokenParameter = '';
if (searchParams.has(SESSION_TOKEN_PARAM_NAME)) {
  sessionTokenParameter = searchParams.get(SESSION_TOKEN_PARAM_NAME) ?? '';
}

let preSelectedDealerIdParameter = '';
if (searchParams.has(DEALER_ID_PARAM_NAME)) {
  preSelectedDealerIdParameter = searchParams.get(DEALER_ID_PARAM_NAME) ?? '';
}

let legalEntityParameter = '';
if (searchParams.has(LEGAL_ENTITY_PARAM_NAME)) {
  legalEntityParameter = searchParams.get(LEGAL_ENTITY_PARAM_NAME) ?? '';
}

export const initialState: RenewalApplicationState = {
  matchMakerAnswers: {
    condition: [],
    typeOfCar: [],
    typeOfGears: [],
    fuelTypes: [],
    expectedDuration: 36,
    expectedMonthlyPayment: MIN_MONTHLY_BUDGET,
    isCompleted: false,
    carSearchType: ICarSearchType.BYTYPE,
    modelOfCar: [],
    specialEquipments: [],
    additionalServices: [],
    financePlan: [],
    noAdditionalDepositCheckbox: true,
    additionalDeposit: 0,
    milesPerYear: 10000
  },
  customerInformation: {
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
    contactPreference: ContactOption.EMAIL
  },
  dealer: {
    name: '',
    street: '',
    zip: '',
    city: '',
    houseNumber: '',
    phone: '',
    id: '',
    email: '',
    regionId: ''
  },
  currentJourneyStatus: JOURNEY_STATE.STARTED,
  transactionId: transactionIdParameter,
  transactionStatus: TransactionStatusType.INITIALIZED,
  sessionToken: sessionTokenParameter,
  preSelectedDealerId: preSelectedDealerIdParameter,
  isEditMode: false,
  legalEntity: legalEntityParameter,
  flowType: FlowType.UNSELECTED,
  editedSections: [],
  skip: false,
  hasSubmittedMM1: false
};

export const AppStateContext = React.createContext<{
  state: RenewalApplicationState;
  dispatch: React.Dispatch<Action>;
}>({
  state: initialState,
  dispatch: () => {}
});

type Action =
  | { type: 'updateState'; state: RenewalApplicationState }
  | { type: 'updateMatchMakerAnswers'; matchMakerAnswers: MatchMakerAnswers }
  | { type: 'updateCustomerInformation'; customer: CustomerContactDetails }
  | { type: 'updateDealer'; dealer?: Dealer }
  | { type: 'updateSessionToken'; sessionToken: string }
  | { type: 'updateTransactionId'; transactionId: string }
  | { type: 'updateJourneyStatus'; journeyStatus: JOURNEY_STATE; isEditMode?: boolean }
  | { type: 'markEditedSection'; sectionName: EditableSummarySection }
  | { type: 'updateEditStatus'; isEditMode: boolean }
  | { type: 'updateSkip'; skip: boolean };

export function reducer(state: RenewalApplicationState, action: Action): RenewalApplicationState {
  switch (action.type) {
    case 'updateState':
      return action.state;

    case 'updateMatchMakerAnswers':
      return {
        ...state,
        matchMakerAnswers: { ...action.matchMakerAnswers }
      };

    case 'updateCustomerInformation':
      return {
        ...state,
        customerInformation: { ...action.customer }
      };

    case 'updateDealer':
      return {
        ...state,
        dealer: action.dealer
      };

    case 'updateTransactionId':
      return {
        ...state,
        transactionId: action.transactionId
      };

    case 'updateSessionToken':
      return {
        ...state,
        sessionToken: action.sessionToken
      };

    case 'updateJourneyStatus':
      if (state.isEditMode) {
        return state;
      }
      return {
        ...state,
        currentJourneyStatus: action.journeyStatus,
        ...(action.isEditMode ? { isEditMode: action.isEditMode } : {})
      };

    case 'updateEditStatus':
      return {
        ...state,
        isEditMode: action.isEditMode
      };

    case 'updateSkip':
      return {
        ...state,
        skip: action.skip
      };

    case 'markEditedSection': {
      const editedSections = state.editedSections;
      editedSections.push(action.sectionName);

      return {
        ...state,
        editedSections: editedSections
      };
    }
  }
}

interface AppProviderProps {
  children: ReactNode;
  mockState?: RenewalApplicationState;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children, mockState }) => {
  const [state, dispatch] = React.useReducer(reducer, mockState || initialState);

  return (
    <AppStateContext.Provider value={{ state, dispatch }}>{children}</AppStateContext.Provider>
  );
};

export const useApplicationState = (): {
  state: RenewalApplicationState;
  dispatch: React.Dispatch<Action>;
} => {
  return React.useContext(AppStateContext);
};
